import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { ApiError } from '../../../../../shared/shared.api';
import { receiptActionDoneMessage, type ReceiptAction } from '../policies/receipt-actions';
import { bounceReceipt, clearReceipt, reverseReceipt, type ReceiptView } from '../receipts.api';

export interface ReceiptActionTarget {
  action: ReceiptAction;
  receipt: Pick<ReceiptView, 'id' | 'reference'>;
}

function send({ action, receipt }: ReceiptActionTarget, value: string) {
  if (action === 'clear') return clearReceipt(receipt.id, { clearedOn: value });
  if (action === 'bounce') return bounceReceipt(receipt.id, { reason: value });
  return reverseReceipt(receipt.id, { reason: value });
}

/**
 * Mark cleared, mark bounced or reverse one receipt, from a dialog. A refusal (409 for a state that moved on, 400 for a date the server
 * rejects) stays inline in the dialog; success toasts. `onChanged` reloads whatever shows the receipt, after a success and after a 409,
 * so the screen shows the state the server holds.
 */
export function useReceiptAction(onChanged: () => Promise<void>) {
  const [target, setTarget] = useState<ReceiptActionTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  function open(action: ReceiptAction, receipt: ReceiptActionTarget['receipt']) {
    setError(null);
    setTarget({ action, receipt });
  }

  /** Returns whether it saved, so the dialog stays open with its error showing when it did not. */
  async function submit(value: string): Promise<boolean> {
    if (!target) return false;
    setError(null);
    setSaving(true);
    try {
      await send(target, value);
      toast.success(receiptActionDoneMessage(target.action, target.receipt.reference));
      await onChanged();
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not save this change — try again.');
      if (err instanceof ApiError && err.status === 409) await onChanged();
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { target, error, saving, open, close: () => setTarget(null), submit };
}
